import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

/**
 * Styleable replacement for the native <select>. Mimics the native keyboard
 * contract (ArrowUp/Down, Home/End, Enter/Space to commit, Escape to close,
 * type-ahead) so it's drop-in compatible, while giving us CSS hooks we don't
 * get with the native control - notably an "open" class on the trigger
 * (so the chevron can flip) and a fully paint-able option list with a
 * real 100%-wide hover highlight.
 */
export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
  disabled?: boolean;
}

interface SelectProps<T extends string> {
  value: T;
  onChange: (v: T) => void;
  options: SelectOption<T>[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
  className?: string;
  minWidth?: number | string;
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  placeholder = "Select...",
  disabled,
  id,
  ariaLabel,
  className,
  minWidth,
}: SelectProps<T>) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const selectedIdx = useMemo(
    () => options.findIndex((o) => o.value === value),
    [options, value],
  );
  const selectedOption = selectedIdx >= 0 ? options[selectedIdx] : null;

  const position = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 4;
    const maxMenuH = 320;
    const pad = 8;
    const w = r.width;
    const spaceBelow = window.innerHeight - r.bottom - pad;
    const openUp = spaceBelow < Math.min(maxMenuH, 180) && r.top > spaceBelow;
    const top = openUp
      ? Math.max(pad, r.top - Math.min(maxMenuH, r.top - pad) - gap)
      : r.bottom + gap;
    let left = r.left;
    const maxLeft = window.innerWidth - w - pad;
    if (left > maxLeft) left = Math.max(pad, maxLeft);
    if (left < pad) left = pad;
    setPlacement({ top, left, width: w });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    position();
    const raf = requestAnimationFrame(() => {
      position();
    });
    const onScroll = () => position();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, position]);

  // Seed activeIndex to the current selection each time we open, and make
  // sure it's visible after the menu mounts.
  useLayoutEffect(() => {
    if (!open) return;
    setActiveIndex(selectedIdx >= 0 ? selectedIdx : 0);
  }, [open, selectedIdx]);

  useLayoutEffect(() => {
    if (!open || activeIndex < 0) return;
    const list = listRef.current;
    if (!list) return;
    const item = list.children[activeIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  // Close on outside interaction.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (listRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer, true);
    return () => document.removeEventListener("pointerdown", onPointer, true);
  }, [open]);

  const commit = (idx: number) => {
    const opt = options[idx];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const stepActive = (delta: number) => {
    if (!options.length) return;
    let i = activeIndex;
    for (let n = 0; n < options.length; n++) {
      i = (i + delta + options.length) % options.length;
      if (!options[i].disabled) break;
    }
    setActiveIndex(i);
  };

  const typeaheadRef = useRef<{ buf: string; clearAt: number }>({
    buf: "",
    clearAt: 0,
  });

  const onTriggerKey = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (
        e.key === "ArrowDown" ||
        e.key === "ArrowUp" ||
        e.key === "Enter" ||
        e.key === " "
      ) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        stepActive(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        stepActive(-1);
        break;
      case "Home":
        e.preventDefault();
        setActiveIndex(options.findIndex((o) => !o.disabled));
        break;
      case "End":
        e.preventDefault();
        for (let i = options.length - 1; i >= 0; i--) {
          if (!options[i].disabled) {
            setActiveIndex(i);
            break;
          }
        }
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (activeIndex >= 0) commit(activeIndex);
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
      case "Tab":
        setOpen(false);
        break;
      default:
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          const now = Date.now();
          const state = typeaheadRef.current;
          if (now > state.clearAt) state.buf = "";
          state.buf += e.key.toLowerCase();
          state.clearAt = now + 600;
          const start = activeIndex >= 0 ? activeIndex : 0;
          for (let k = 1; k <= options.length; k++) {
            const i = (start + k) % options.length;
            const o = options[i];
            if (!o.disabled && o.label.toLowerCase().startsWith(state.buf)) {
              setActiveIndex(i);
              break;
            }
          }
        }
    }
  };

  const listboxId = id ? `${id}-listbox` : undefined;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        id={id}
        className={`cs-trigger${open ? " open" : ""}${className ? ` ${className}` : ""}`}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        disabled={disabled}
        style={minWidth != null ? { minWidth } : undefined}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onTriggerKey}
      >
        <span className={`cs-value${selectedOption ? "" : " placeholder"}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="cs-chevron" aria-hidden>
          <svg width="10" height="6" viewBox="0 0 10 6">
            <path
              d="M1 1l4 4 4-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {open && placement &&
        createPortal(
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            className="cs-menu"
            tabIndex={-1}
            style={{
              top: placement.top,
              left: placement.left,
              minWidth: placement.width,
            }}
          >
            {options.map((o, i) => {
              const isSelected = o.value === value;
              const isActive = i === activeIndex;
              return (
                <li
                  key={o.value}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={o.disabled || undefined}
                  className={`cs-option${isActive ? " active" : ""}${isSelected ? " selected" : ""}${o.disabled ? " disabled" : ""}`}
                  onPointerEnter={() => !o.disabled && setActiveIndex(i)}
                  onPointerDown={(e) => {
                    // Prevent blur on the trigger before commit fires.
                    e.preventDefault();
                    if (!o.disabled) commit(i);
                  }}
                >
                  <span className="cs-option-label">{o.label}</span>
                  {isSelected && (
                    <span className="cs-option-check" aria-hidden>
                      <svg width="12" height="9" viewBox="0 0 12 9">
                        <path
                          d="M1 4.5 4.3 8 11 1"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>,
          document.body,
        )}
    </>
  );
}
